import { useEffect, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  sRGBEncoding,
  LinearFilter,
  Color,
  TextureLoader,
  Vector3,
  MathUtils,
  Group,
  AmbientLight,
  DirectionalLight,
  WebGLRenderer,
  PerspectiveCamera,
  Scene,
  WebGLRenderTarget,
  PlaneBufferGeometry,
  MeshBasicMaterial,
  Mesh,
  OrthographicCamera,
  MeshDepthMaterial,
  ShaderMaterial,
} from 'three';
import { HorizontalBlurShader } from 'three/examples/jsm/shaders/HorizontalBlurShader.js';
import { VerticalBlurShader } from 'three/examples/jsm/shaders/VerticalBlurShader.js';
// GLTFLoader is used to load the .glb files (iphone-11.glb and macbook-pro.glb)
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
// Popmotion is used to animate the laptop opening and phone spring-up animations
// animate laptop and phone with popmotion 
//  look the example !!! https://popmotion.io/pure/
import { delay, chain, spring, value } from 'popmotion';
import { getImageFromSrcSet } from 'utils/image';
import { useInViewport, usePrefersReducedMotion } from 'hooks';
import { cleanScene, cleanRenderer, removeLights } from 'utils/three';
import { ModelAnimationType } from './deviceModels';
import { numToMs } from 'utils/style';
import './index.css';

// These mesh types correspond to named objects inside the .glb files
// The 3D models (iphone-11.glb and macbook-pro.glb) contain meshes with these specific names
const MeshType = {
  Frame: 'Frame',     // The laptop's screen frame that rotates during the opening animation
  Logo: 'Logo',       // Brand logos on the devices
  Screen: 'Screen',   // The screen surfaces where textures (images) are applied
};

const Model = ({
  models,              // Array of model configurations (phone/laptop with textures)
  show = true,
  showDelay = 0,
  cameraPosition = { x: 0, y: 0, z: 8 },
  enableControls,
  style,
  className,
  alt,
  ...rest
}) => {
  const [modelData, setModelData] = useState();
  const [loaded, setLoaded] = useState(false);
  
  // React refs to store Three.js objects persistently across renders
  const container = useRef();
  const canvas = useRef();
  const camera = useRef();
  const textureLoader = useRef();    // Loads 2D images to apply on 3D model screens
  const modelLoader = useRef();      // GLTFLoader instance for loading .glb files
  const modelGroup = useRef();       // Groups all 3D models together
  const scene = useRef();
  const renderer = useRef();
  
  // Shadow system refs - creates realistic shadows beneath the 3D models
  const shadowGroup = useRef();
  const renderTarget = useRef();
  const renderTargetBlur = useRef();
  const shadowCamera = useRef();
  const depthMaterial = useRef();
  const horizontalBlurMaterial = useRef();
  const verticalBlurMaterial = useRef();
  const plane = useRef();
  const lights = useRef();
  const blurPlane = useRef();
  const fillPlane = useRef();
  
  const isInViewport = useInViewport(container, false, { threshold: 0.4 });
  const reduceMotion = usePrefersReducedMotion();

  // Main initialization effect - sets up the entire Three.js scene
  useEffect(() => {
    // Initialize Three.js core components
    const { clientWidth, clientHeight } = container.current;

    // WebGL renderer setup for high-quality 3D rendering
    renderer.current = new WebGLRenderer({
      canvas: canvas.current,
      alpha: true,                    // Transparent background
      antialias: false,               // Disabled for performance
      powerPreference: 'high-performance',
    });

    renderer.current.setPixelRatio(2);
    renderer.current.setSize(clientWidth, clientHeight);
    renderer.current.outputEncoding = sRGBEncoding;  // Proper color space
    renderer.current.physicallyCorrectLights = true; // Realistic lighting

    // Camera setup - perspective view for 3D depth
    camera.current = new PerspectiveCamera(36, clientWidth / clientHeight, 0.1, 100);
    camera.current.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
    scene.current = new Scene();

    // Loaders initialization
    textureLoader.current = new TextureLoader();  // For loading screen images
    modelLoader.current = new GLTFLoader();       // For loading .glb 3D models
    modelGroup.current = new Group();             // Container for all 3D models

    // Lighting
    const ambientLight = new AmbientLight(0xffffff, 1.2);
    const keyLight = new DirectionalLight(0xffffff, 1.1);
    const fillLight = new DirectionalLight(0xffffff, 0.8);

    fillLight.position.set(-6, 2, 2);
    keyLight.position.set(0.5, 0, 0.866);
    lights.current = [ambientLight, keyLight, fillLight];
    lights.current.forEach(light => scene.current.add(light));

    // The shadow container, if you need to move the plane just move this
    shadowGroup.current = new Group();
    scene.current.add(shadowGroup.current);
    shadowGroup.current.position.set(0, 0, -0.8);
    shadowGroup.current.rotateX(Math.PI / 2);

    const renderTargetSize = 512;
    const planeWidth = 8;
    const planeHeight = 8;
    const cameraHeight = 1.5;
    const shadowOpacity = 0.8;
    const shadowDarkness = 3;

    // The render target that will show the shadows in the plane texture
    renderTarget.current = new WebGLRenderTarget(renderTargetSize, renderTargetSize);
    renderTarget.current.texture.generateMipmaps = false;

    // The render target that we will use to blur the first render target
    renderTargetBlur.current = new WebGLRenderTarget(renderTargetSize, renderTargetSize);
    renderTargetBlur.current.texture.generateMipmaps = false;

    // Make a plane and make it face up
    const planeGeometry = new PlaneBufferGeometry(planeWidth, planeHeight).rotateX(
      Math.PI / 2
    );

    const planeMaterial = new MeshBasicMaterial({
      map: renderTarget.current.texture,
      opacity: shadowOpacity,
      transparent: true,
    });

    plane.current = new Mesh(planeGeometry, planeMaterial);
    // The y from the texture is flipped!
    plane.current.scale.y = -1;
    shadowGroup.current.add(plane.current);

    // The plane onto which to blur the texture
    blurPlane.current = new Mesh(planeGeometry);
    blurPlane.current.visible = false;
    shadowGroup.current.add(blurPlane.current);

    // The plane with the color of the ground
    const fillMaterial = new MeshBasicMaterial({
      color: 0xffffff,
      opacity: 0,
      transparent: true,
    });

    fillPlane.current = new Mesh(planeGeometry, fillMaterial);
    fillPlane.current.rotateX(Math.PI);
    fillPlane.current.position.y -= 0.00001;
    shadowGroup.current.add(fillPlane.current);

    // The camera to render the depth material from
    shadowCamera.current = new OrthographicCamera(
      -planeWidth / 2,
      planeWidth / 2,
      planeHeight / 2,
      -planeHeight / 2,
      0,
      cameraHeight
    );
    // Get the camera to look up
    shadowCamera.current.rotation.x = Math.PI / 2;
    shadowGroup.current.add(shadowCamera.current);

    // Like MeshDepthMaterial, but goes from black to transparent
    depthMaterial.current = new MeshDepthMaterial();
    depthMaterial.current.userData.darkness = { value: shadowDarkness };
    depthMaterial.current.onBeforeCompile = shader => {
      shader.uniforms.darkness = depthMaterial.current.userData.darkness;
      shader.fragmentShader = `
        uniform float darkness;
        ${shader.fragmentShader.replace(
          'gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );',
          'gl_FragColor = vec4( vec3( 0.0 ), ( 1.0 - fragCoordZ ) * darkness );'
        )}
      `;
    };
    depthMaterial.current.depthTest = false;
    depthMaterial.current.depthWrite = false;

    horizontalBlurMaterial.current = new ShaderMaterial(HorizontalBlurShader);
    horizontalBlurMaterial.current.depthTest = false;

    verticalBlurMaterial.current = new ShaderMaterial(VerticalBlurShader);
    verticalBlurMaterial.current.depthTest = false;

    /**
     * TEXTURE APPLICATION FUNCTION
     * This function applies 2D images to the Screen meshes of the 3D models
     * Both iphone-11.glb and macbook-pro.glb have Screen meshes that display content
     */
    const applyScreenTexture = async (texture, node) => {
      texture.encoding = sRGBEncoding;
      texture.minFilter = LinearFilter;
      texture.magFilter = LinearFilter;
      texture.flipY = false;  // Important: GLB textures don't need Y-flipping
      texture.anisotropy = renderer.current.capabilities.getMaxAnisotropy();
      texture.generateMipmaps = false;

      // Decode the texture to prevent jank on first render
      await renderer.current.initTexture(texture);

      // Apply the texture to the screen mesh
      node.material.color = new Color(0xffffff);
      node.material.transparent = false;
      node.material.map = texture;  // This is where the image appears on the 3D model
    };

    // Build an array of promises to fetch and apply models & animations
        /**
     * MODEL LOADING AND PROCESSING
     * This is where the magic happens - loading both iphone-11.glb and macbook-pro.glb
     * and preparing them for display with textures and animations
     */
    const modelConfigPromises = models.map(async (model, index) => {
      const { texture, position, url } = model;  // url points to .glb file
      let loadFullResTexture;

      // Load both the 3D model (.glb) and placeholder image simultaneously
      const [gltf, placeholder] = await Promise.all([
        await modelLoader.current.loadAsync(url),  // This loads iphone-11.glb or macbook-pro.glb
        await textureLoader.current.loadAsync(texture.placeholder),
      ]);

      /**
       * MESH PROCESSING
       * Traverse through all meshes in the loaded .glb file
       * Each .glb file contains multiple meshes (Frame, Screen, Logo, etc.)
       */
      gltf.scene.traverse(async node => {
        if (node.material) {
          // Set base color for all materials (dark gray)
          node.material.color = new Color(0x1f2025);
          node.material.color.convertSRGBToLinear();
        }

        // Find the Screen mesh and apply textures to it
        // This is how project screenshots appear on the phone/laptop screens
        if (node.name === MeshType.Screen) {
          // Apply placeholder image first for quick loading
          applyScreenTexture(placeholder, node);
          
          // Prepare function to load high-resolution image later
          loadFullResTexture = async () => {
            const image = await getImageFromSrcSet(texture);
            const fullSize = await textureLoader.current.loadAsync(image);
            await applyScreenTexture(fullSize, node);  // Replace with high-res image
          };
        }
      });

      // Add the loaded 3D model to the scene
      modelGroup.current.add(gltf.scene);

      // Set up animations based on model type (phone: spring-up, laptop: opening)
      const animation = getModelAnimation({
        model,
        gltf,        // The loaded 3D model data
        position,
        reduceMotion,
        renderFrame,
        index,
        showDelay,
      });

      return { ...animation, loadFullResTexture };
    });

    setModelData(modelConfigPromises);

    return () => {
      removeLights(lights.current);
      cleanScene(scene.current);
      cleanRenderer(renderer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const blurShadow = useCallback(amount => {
    blurPlane.current.visible = true;

    // Blur horizontally and draw in the renderTargetBlur
    blurPlane.current.material = horizontalBlurMaterial.current;
    blurPlane.current.material.uniforms.tDiffuse.value = renderTarget.current.texture;
    horizontalBlurMaterial.current.uniforms.h.value = amount * (1 / 256);

    renderer.current.setRenderTarget(renderTargetBlur.current);
    renderer.current.render(blurPlane.current, shadowCamera.current);

    // Blur vertically and draw in the main renderTarget
    blurPlane.current.material = verticalBlurMaterial.current;
    blurPlane.current.material.uniforms.tDiffuse.value = renderTargetBlur.current.texture;
    verticalBlurMaterial.current.uniforms.v.value = amount * (1 / 256);

    renderer.current.setRenderTarget(renderTarget.current);
    renderer.current.render(blurPlane.current, shadowCamera.current);

    blurPlane.current.visible = false;
  }, []);

  // Handle render passes for a single frame
  const renderFrame = useCallback(() => {
    const blurAmount = 5;

    // Remove the background
    const initialBackground = scene.current.background;
    scene.current.background = null;

    // Force the depthMaterial to everything
    // cameraHelper.visible = false;
    scene.current.overrideMaterial = depthMaterial.current;

    // Render to the render target to get the depths
    renderer.current.setRenderTarget(renderTarget.current);
    renderer.current.render(scene.current, shadowCamera.current);

    // And reset the override material
    scene.current.overrideMaterial = null;

    blurShadow(blurAmount);

    // A second pass to reduce the artifacts
    // (0.4 is the minimum blur amout so that the artifacts are gone)
    blurShadow(blurAmount * 0.4);

    // Reset and render the normal scene
    renderer.current.setRenderTarget(null);
    scene.current.background = initialBackground;

    renderer.current.render(scene.current, camera.current);
  }, [blurShadow]);

  // Load and animate models when component becomes visible
  useEffect(() => {
    let introSprings = [];

    if (!modelData) return;

    scene.current.add(modelGroup.current);

    /**
     * SCENE LOADING AND ANIMATION EXECUTION
     * This handles the sequential loading and animation of 3D models
     */
    const loadScene = async () => {
      // Wait for all models to load (both iphone-11.glb and macbook-pro.glb if both present)
      const loadedModels = await Promise.all(modelData);

      setLoaded(true);

      // Process each loaded model
      const handleModelLoad = loadedModels.map(async model => {
        // Start entrance animation (spring-up for phone, opening for laptop)
        if (model.animation) {
          const modelAnimation = model.animation.start(model.modelValue);
          introSprings.push(modelAnimation);
        }

        if (reduceMotion) {
          renderFrame();
        }

        // Load high-resolution screen texture after model is visible
        await model.loadFullResTexture();

        // Render the updated texture
        if (reduceMotion) {
          renderFrame();
        }
      });

      await Promise.all(handleModelLoad);
    };

    if (show) {
      loadScene();
    }

    return () => {
      // Clean up animations
      for (const spring of introSprings) {
        spring.stop();
      }
    };
  }, [modelData, reduceMotion, renderFrame, show]);

  /**
   * INTERACTIVE MOUSE MOVEMENT
   * Creates subtle rotation of 3D models based on mouse position
   * Works for both iPhone and MacBook models
   */
  useEffect(() => {
    let rotationSpring;
    let rotationSpringValue;

    const onMouseMove = event => {
      const { rotation } = modelGroup.current;
      const { innerWidth, innerHeight } = window;

      // Calculate mouse position relative to screen center (-0.5 to 0.5)
      const position = {
        x: (event.clientX - innerWidth / 2) / innerWidth,
        y: (event.clientY - innerHeight / 2) / innerHeight,
      };

      if (!rotationSpringValue) {
        rotationSpringValue = value({ x: rotation.x, y: rotation.y }, ({ x, y }) => {
          rotation.set(x, y, rotation.z);
          renderFrame();
        });
      }

      // Animate rotation smoothly following mouse movement
      rotationSpring = spring({
        from: rotationSpringValue.get(),
        to: { x: position.y / 2, y: position.x / 2 },  // Reduced sensitivity
        stiffness: 40,
        damping: 40,
        velocity: rotationSpringValue.getVelocity(),
        restSpeed: 0.00001,
        mass: 1.4,
      }).start(rotationSpringValue);
    };

    if (isInViewport && !reduceMotion) {
      window.addEventListener('mousemove', onMouseMove);
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      rotationSpring?.stop();
    };
  }, [isInViewport, reduceMotion, renderFrame]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!container.current) return;

      const { clientWidth, clientHeight } = container.current;

      renderer.current.setSize(clientWidth, clientHeight);
      camera.current.aspect = clientWidth / clientHeight;
      camera.current.updateProjectionMatrix();

      renderFrame();
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [renderFrame]);

  return (
    <div
      className={classNames('model', { 'model--loaded': loaded }, className)}
      style={{ '--delay': numToMs(showDelay), ...style }}
      ref={container}
      role="img"
      aria-label={alt}
      {...rest}
    >
      <canvas className="model__canvas" ref={canvas} />
    </div>
  );
};

/**
 * MODEL ANIMATION CONFIGURATIONS
 * Defines specific animations for different device types
 * - iPhone: Spring-up animation (slides up from below)
 * - MacBook: Opening animation (screen rotates from closed to open position)
 */
function getModelAnimation({ model, gltf, reduceMotion, renderFrame, index, showDelay }) {
  const positionVector = new Vector3(
    model.position.x,
    model.position.y,
    model.position.z
  );

  if (reduceMotion) {
    gltf.scene.position.set(...positionVector.toArray());
    return;
  }

  /**
   * IPHONE SPRING-UP ANIMATION
   * 
   * iPhone-11.gltf Structure (from actual file):
   * ├── Node 0: "Frame" (iPhone body/bezel)
   * ├── Node 1: "Screen" (display surface - textures applied here)
   * └── Node 2: "Logo" (Apple logo)
   * 
   * Animation: Entire phone model slides up from below with spring physics
   * All nodes move together as one unit (no individual node rotation)
   */
  if (model.animation === ModelAnimationType.SpringUp) {
    const startPosition = new Vector3(
      positionVector.x,
      positionVector.y - 1,  // Start 1 unit below final position
      positionVector.z
    );
    const endPosition = positionVector;

    gltf.scene.position.set(...startPosition.toArray());

    // Create animation value that updates position and triggers re-render
    const modelValue = value(gltf.scene.position, ({ x, y, z }) => {
      gltf.scene.position.set(x, y, z);
      renderFrame();
    });

    // Chain delay + spring animation
    const animation = chain(
      delay(300 * index + showDelay * 0.6),  // Stagger multiple models
      spring({
        from: startPosition,
        to: endPosition,
        stiffness: 60,   // Snappy animation
        damping: 16,     // Some bounce
        restSpeed: 0.001,
      })
    );

    return { animation, modelValue };
  }

  /**
   * MACBOOK OPENING ANIMATION
   * 
   * MacBook-Pro.gltf Structure (from actual file):
   * ├── Node 2: "Keyboard" (base assembly - stays stationary)
   * │   ├── Node 0: "Body" (laptop body)
   * │   └── Node 1: "Touchbar"
   * └── Node 5: "Frame" ⭐ (screen assembly - THIS ROTATES)
   *     ├── Node 3: "Logo" (Apple logo on screen back)
   *     └── Node 4: "Screen" (display surface - textures applied here)
   * 
   * Animation: Only the "Frame" node rotates around X-axis (laptop hinge motion)
   * The screen assembly opens from 90° (closed) to 0° (open)
   */
  if (model.animation === ModelAnimationType.LaptopOpen) {
    // Find the Frame mesh (the laptop screen that needs to rotate)
    const frameNode = gltf.scene.children.find(node => node.name === MeshType.Frame);
    const startRotation = new Vector3(MathUtils.degToRad(90), 0, 0);  // Closed position
    const endRotation = new Vector3(0, 0, 0);                         // Open position

    // Position the laptop but start with closed screen
    gltf.scene.position.set(...positionVector.toArray());
    frameNode.rotation.set(...startRotation.toArray());

    // Create animation value for rotation
    const modelValue = value(frameNode.rotation, ({ x, y, z }) => {
      frameNode.rotation.set(x, y, z);
      renderFrame();
    });

    // Smooth opening animation with slight delay
    const animation = chain(
      delay(300 * index + showDelay + 200),  // Extra delay for laptop
      spring({
        from: startRotation,
        to: endRotation,
        stiffness: 50,    // Smooth opening
        damping: 14,      // Realistic deceleration
        restSpeed: 0.001,
      })
    );

    return { animation, modelValue };
  }
}

export default Model;

/**
 * COMPLETE GLTF STRUCTURE ANALYSIS (ACTUAL FILE DATA)
 * ===================================================
 * 
 * iPhone-11.gltf Structure (Flat hierarchy - all nodes at root level):
 * ────────────────────────────────────────────────────────────────────
 * Scene Root
 * ├── Node 0: "Frame" (iPhone body/frame - material: Frame)
 * ├── Node 1: "Screen" (display surface - material: Screen with baseColorTexture)
 * └── Node 2: "Logo" (Apple logo - material: Logo with metallic finish)
 * 
 * MacBook-Pro.gltf Structure (Hierarchical - realistic laptop mechanics):
 * ──────────────────────────────────────────────────────────────────────
 * Scene Root
 * ├── Node 2: "Keyboard" (laptop base assembly - stationary during animation)
 * │   ├── Node 0: "Body" (main laptop body - material: Frame)
 * │   └── Node 1: "Touchbar" (MacBook Pro touchbar - material: Frame)
 * └── Node 5: "Frame" ⭐ (screen assembly - THE ANIMATION TARGET)
 *     ├── Node 3: "Logo" (Apple logo on screen back - material: Logo)
 *     └── Node 4: "Screen" (display surface - material: Screen with baseColorTexture)
 * 
 * ANIMATION MECHANICS BREAKDOWN:
 * =============================
 * 
 * iPhone SpringUp Animation:
 * ─────────────────────────
 * • Target: Entire gltf.scene (all nodes move together)
 * • Type: Y-axis translation (vertical movement)
 * • From: position.y - 1 (below viewport)
 * • To: position.y (final position)
 * • Physics: Spring with stiffness: 60, damping: 16 (bouncy)
 * • Visual: Phone slides up from bottom with slight bounce
 * 
 * MacBook LaptopOpen Animation:
 * ────────────────────────────
 * • Target: Only the "Frame" node (screen assembly)
 * • Type: X-axis rotation (around laptop hinge)
 * • From: 90° rotation (screen closed, lying flat)
 * • To: 0° rotation (screen open, upright)
 * • Physics: Spring with stiffness: 50, damping: 14 (smooth)
 * • Visual: Realistic laptop opening motion
 * 
 * TEXTURE MAPPING SYSTEM:
 * ======================
 * 
 * Both models use "Screen" meshes for displaying project content:
 * 
 * iPhone Screen Mesh:
 * • Material index: 1 (Screen material)
 * • Has baseColorTexture reference (index: 0)
 * • TEXCOORD_0 attributes for UV mapping
 * • Images applied: Project screenshots, app interfaces
 * 
 * MacBook Screen Mesh:
 * • Material index: 2 (Screen material) 
 * • Has baseColorTexture reference (index: 0)
 * • TEXCOORD_0 attributes for UV mapping
 * • Images applied: Web project screenshots, dashboards
 * 
 * RENDERING PIPELINE:
 * ==================
 * 
 * 1. GLTFLoader loads .glb files → Three.js scene graph
 * 2. Scene traversal finds nodes by name ("Screen", "Frame", "Logo")
 * 3. TextureLoader loads 2D images (placeholder → high-res)
 * 4. applyScreenTexture() maps images to Screen mesh materials
 * 5. Animation system updates node transforms each frame
 * 6. WebGL renderer draws updated scene to canvas
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * =========================
 * 
 * • Progressive texture loading (placeholder → full resolution)
 * • Viewport intersection detection (only animate when visible)
 * • Reduced motion support (accessibility)
 * • Shadow rendering with blur optimization
 * • Event-driven re-rendering (not continuous)
 */